import React from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InsightsIcon from '@mui/icons-material/Insights';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import logoLarge from '../mfs-logo.png';

// const featureCards = [
//   {
//     title: 'Document Management',
//     description: 'Upload, organize, and track all your compliance documents in one secure location.',
//     icon: <AssignmentTurnedInIcon fontSize="large" color="primary" />,
//   },
//   {
//     title: 'Expiration Tracking',
//     description: 'Never miss a renewal with automated alerts and visual status indicators.',
//     icon: <AccessTimeIcon fontSize="large" sx={{ color: '#f8c94c' }} />,
//   },
//   {
//     title: 'Compliance Dashboard',
//     description: 'Get real-time insights into your compliance status across every location.',
//     icon: <InsightsIcon fontSize="large" color="info" />,
//   },
// ];

const LandingPage = ({ themeMode = 'dark', onToggleTheme = () => {} }) => {
  const theme = useTheme();
  const gradientBackground = theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(30,30,35,1) 0%, rgba(18,18,20,1) 100%)'
    : 'linear-gradient(180deg, #eef4ff 0%, #ffffff 75%)';

  const services = [
    {
      heading: 'Food Safety Training',
      description: 'ISO, HACCP, FoSTaC, GMP, GHP, GLP, GAP certifications along with technical and regulatory consultation tailored to your operations.',
    },
    {
      heading: 'Third Party Auditing & Implementation',
      description: 'Independent audits with actionable roadmaps to align with national and international food safety standards.',
    },
    {
      heading: 'Hygiene Rating Certification',
      description: 'End-to-end guidance to achieve and maintain FSSAI hygiene ratings across outlets and branches.',
    },
    {
      heading: 'FSSAI Licensing and Registration',
      description: 'Expert assistance through documentation, inspections, and renewals with proactive compliance monitoring.',
    },
    {
      heading: 'ISO Training, Auditing & Certification',
      description: 'Comprehensive ISO programs from awareness sessions to internal audits, gap analysis, and certification support.',
    },
    {
      heading: 'Food & Water Testing, Air Quality Monitoring, Pest Control',
      description: 'Certified laboratory testing, routine air monitoring, and integrated pest management for a safer workspace.',
    },
    {
      heading: 'Industrial & Institutional Project Implementation',
      description: 'Turnkey safety and hygiene projects for manufacturing units, hospitals, schools, and hospitality establishments.',
    },
    {
      heading: 'Commercial Kitchen Designing',
      description: 'Workflow-optimized kitchen layouts that comply with food safety norms and enhance operational efficiency.',
    },
    {
      heading: 'Electronic Device & Machinery Calibrations',
      description: 'Calibration services for critical equipment ensuring accuracy, consistency, and regulatory compliance.',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: gradientBackground,
        color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Stack spacing={6}>
          <TopBar themeMode={themeMode} />

          <HeroSection />

          <ServicesGrid services={services} />

          {/* <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 3,
            }}
          >
            {featureCards.map((card) => (
              <Paper
                key={card.title}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0)' : 'rgba(255, 255, 255, 0)',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {card.icon}
                </Box>
                <Typography variant="h6" gutterBottom>{card.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </Paper>
            ))}
          </Box> */}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              textAlign: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#ffffff',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0)' : '1px solid rgba(15,23,42,0.08)',
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography variant="h5" fontWeight={600}>Ready to Ensure 100% Compliance?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                Join businesses that trust Mister Food Safety for their compliance management needs.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component="a"
                href="/login"
                sx={{ px: 4, py: 1.25 }}
              >
                Sign In to Continue
              </Button>
            </Stack>
          </Paper>

          <AboutSection />

          <ThemeToggleBar themeMode={themeMode} onToggleTheme={onToggleTheme} />
        </Stack>
      </Container>
    </Box>
  );
};

const TopBar = ({ themeMode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: 4,
        p: { xs: 2, md: 3 },
        backgroundColor: 'rgba(255, 255, 255, 0)',
        backdropFilter: 'blur(12px)',
        border: (theme) => theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(15,23,42,0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          component="img"
          src={logoLarge}
          alt="Mister Food Safety"
          sx={{
            height: 40,
            width: 'auto',
            objectFit: 'contain',
            filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
            transition: 'filter 0.2s ease',
          }}
        />
      </Box>
      <Button
        variant="contained"
        component="a"
        href="/login"
        sx={{ px: 3 }}
      >
        Sign In
      </Button>
    </Box>
  );
};

const HeroSection = () => (
  <Stack spacing={3} alignItems="center" textAlign="center">
    <Typography variant="h3" fontWeight={700} sx={{ maxWidth: 760 }}>
      Streamline Your Food Safety Compliance
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
      Track documents, monitor expiration dates, and ensure 100% compliance across all your business
      locations with our comprehensive management system.
    </Typography>
    <Button
      variant="contained"
      size="large"
      color="primary"
      component="a"
      href="tel:+917708054431"
      startIcon={<CallRoundedIcon />}
      sx={{ px: 4, py: 1.25 }}
    >
      Talk to our Experts
    </Button>
  </Stack>
);

const ServicesGrid = ({ services }) => (
  <Paper
    elevation={0}
    sx={(theme) => ({
      borderRadius: 4,
      p: { xs: 3, md: 5 },
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(255,255,255,0.9)',
      border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255,255,255,0.04)'
        : '1px solid rgba(15,23,42,0.05)',
      boxShadow: theme.palette.mode === 'dark' ? 'none' : theme.shadows[4],
    })}
  >
    <Stack spacing={1.5} textAlign="center">
      <Typography variant="overline" letterSpacing={2} color="primary">
        Our Services
      </Typography>
      <Typography variant="h4" fontWeight={700}>
        Comprehensive Food Safety & Compliance Solutions
      </Typography>
      {/* <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ maxWidth: 720, mx: 'auto' }}
      >
        From training to certification, we deliver a 360° compliance framework tailored for food businesses,
        hospitality, and institutional operations.
      </Typography> */}
    </Stack>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
        gap: { xs: 2, md: 3 },
        mt: 4,
      }}
    >
      {services.map((service) => (
        <Paper
          key={service.heading}
          elevation={0}
          sx={(theme) => ({
            borderRadius: 3,
            p: { xs: 2.5, md: 3 },
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(129,140,248,0.12) 0%, rgba(15,23,42,0.72) 100%)'
              : 'linear-gradient(180deg, rgba(129,140,248,0.12) 0%, #ffffff 90%)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(129,140,248,0.25)'
              : '1px solid rgba(129,140,248,0.18)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            boxShadow: theme.palette.mode === 'dark' ? 'none' : theme.shadows[2],
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.palette.mode === 'dark' ? theme.shadows[4] : theme.shadows[6],
            },
          })}
        >
          <Typography variant="h6" fontWeight={700}>
            {service.heading}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {service.description}
          </Typography>
        </Paper>
      ))}
    </Box>
  </Paper>
);

const AboutSection = () => (
  <Paper
    elevation={0}
    sx={(theme) => ({
      borderRadius: 4,
      p: { xs: 3, md: 5 },
      mt: 4,
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(129,140,248,0.18) 0%, rgba(67,56,202,0.6) 100%)'
        : 'linear-gradient(180deg, rgba(129,140,248,0.1) 0%, rgba(67,56,202,0.18) 100%)',
      color: theme.palette.mode === 'dark' ? '#f9f9ff' : '#1f1a40',
      border: '1px solid rgba(129,140,248,0.2)',
      boxShadow: theme.palette.mode === 'dark' ? 'none' : theme.shadows[4],
    })}
  >
    <Stack spacing={2.5} textAlign="left">
      <Typography variant="h4" fontWeight={700}>
        About Us
      </Typography>
      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
        Mister Food Safety (OPC) Private Limited is a Registered ISO 9001:2015 Certified Food Safety Consultation Firm.
        We serve across the globe, providing tailored solutions for all your food safety needs.
      </Typography>
      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
        We support you specifically in your technical and regulatory requirements. Our pricing is affordable and
        benchmarked against government and market rates. We believe that delivering high-quality service builds
        responsible food businesses and contributes to sustainable national growth.
      </Typography>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(15,23,42,0.6)'
            : 'rgba(255,255,255,0.85)',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.12)'
            : 'rgba(99,102,241,0.2)',
        })}
      >
        <Stack spacing={1}
          sx={(theme) => ({
            textAlign: { xs: 'center', sm: 'left' },
            color: theme.palette.mode === 'dark' ? '#f1f5ff' : '#312e81',
          })}
        >
          <Typography variant="subtitle1" fontWeight={700} letterSpacing={0.5}>
            Contact For Queries
          </Typography>
          <Stack spacing={0.5}>
            <Typography component="a" href="tel:+917708054431" sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }}>
              +91 77080 54431
            </Typography>
            <Typography component="a" href="tel:+917092492494" sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }}>
              +91 70924 92494
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  </Paper>
);

const ThemeToggleBar = ({ themeMode, onToggleTheme }) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      mt: 4,
      px: { xs: 2, md: 0 },
      py: 2,
      borderRadius: 3,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(15,23,42,0.06)',
      border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255,255,255,0.08)'
        : '1px solid rgba(15,23,42,0.08)',
    })}
  >
    <Typography variant="body2" color="text.secondary">
      Prefer a different look?
    </Typography>
    <Button
      variant="contained"
      color="primary"
      onClick={onToggleTheme}
      startIcon={themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      sx={{ px: 3, borderRadius: 2 }}
    >
      Switch to {themeMode === 'dark' ? 'Light' : 'Dark'} Mode
    </Button>
  </Box>
);

export default LandingPage;
